import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchLectureData } from '../services/googleSheets';
import DesktopLayout from '../layouts/DesktopLayout';
import MobileLayout from '../layouts/MobileLayout';
import { useConfig } from '../config/ConfigContext';
import FloatingShareButton from '../components/FloatingShareButton';
import { extractVideoId } from '../utils/youtubeUtils';

// Simple mobile detection using window width
const isMobile = () => window.innerWidth <= 768;

/**
 * Page for displaying all lectures for a specific event
 * @returns {JSX.Element}
 */
const EventPage = () => {
  const { eventName } = useParams();
  const { config, loading: configLoading, error: configError } = useConfig();
  const [lectures, setLectures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOption, setSortOption] = useState('lectureIdInSeries-asc');
  const [filterTag, setFilterTag] = useState('');

  // Define theme based on config or use defaults
  const theme = {
    colors: {
      primary: config?.colors?.primary || '#1e4273',
      secondary: config?.colors?.secondary || '#f5f7fa',
      accent: config?.colors?.accent || '#e67e22',
      text: '#333333',
      darkGray: '#666666',
      mediumGray: '#999999',
      lightGray: '#f0f2f5',
      background: config?.colors?.background || '#f8f9fa',
      success: '#27ae60',
      error: '#e74c3c',
      warning: '#f39c12'
    },
    borderRadius: {
      small: '4px',
      medium: '8px',
      large: '12px',
      circle: '50%'
    },
    spacing: {
      xs: '4px',
      sm: '8px',
      md: '16px',
      lg: '24px',
      xl: '32px',
      xxl: '48px'
    }
  };

  // Decode the event name from URL parameter
  const decodedEventName = decodeURIComponent(eventName);

  // Get all unique tags from lectures
  const getTags = () => {
    const allTags = lectures
      .flatMap(lecture => lecture.tags || [])
      .filter(tag => tag); // Remove null/undefined/empty

    return [...new Set(allTags)];
  };

  // Sort and filter lectures based on user selection
  const getFilteredAndSortedLectures = () => {
    // Apply tag filter if selected
    let filtered = lectures;
    if (filterTag) {
      filtered = lectures.filter(lecture => 
        lecture.tags && lecture.tags.includes(filterTag)
      );
    }

    // Apply sorting
    return [...filtered].sort((a, b) => {
      const [field, direction] = sortOption.split('-');

      if (field === 'lectureIdInSeries') {
        // First try to sort by lectureIdInSeries if available
        if (a.lectureIdInSeries && b.lectureIdInSeries) {
          const aId = parseInt(a.lectureIdInSeries.replace(/\D/g, '')) || 0;
          const bId = parseInt(b.lectureIdInSeries.replace(/\D/g, '')) || 0;
          return direction === 'asc' ? aId - bId : bId - aId;
        }
        // Fall back to global lectureId otherwise
        const aId = parseInt(a.lectureId.replace(/\D/g, '')) || 0;
        const bId = parseInt(b.lectureId.replace(/\D/g, '')) || 0;
        return direction === 'asc' ? aId - bId : bId - aId;
      }

      if (field === 'date') {
        const aDate = new Date(a.date || '1970-01-01');
        const bDate = new Date(b.date || '1970-01-01');
        return direction === 'asc' ? aDate - bDate : bDate - aDate;
      }

      if (field === 'title') {
        const aTitle = a.title || '';
        const bTitle = b.title || '';
        return direction === 'asc' 
          ? aTitle.localeCompare(bTitle) 
          : bTitle.localeCompare(aTitle);
      }

      return 0;
    });
  };

  useEffect(() => {
    const loadEventLectures = async () => {
      // Wait for config and eventName
      if (configLoading || !config || !eventName) {
        console.log('EventPage: Waiting for config/eventName...');
        return;
      }
      // Handle config error
      if (configError) {
        console.error('EventPage: Config loading failed:', configError);
        setError(`Failed to load configuration: ${configError.message || configError}`);
        setLoading(false);
        return;
      }
      // Ensure google_sheet_id is available
      if (!config.google_sheet_id) {
        console.error('EventPage: google_sheet_id is missing from config.');
        setError('Spreadsheet ID is missing in the configuration.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log(`EventPage: Fetching data for sheet ID: ${config.google_sheet_id}, Event: ${decodedEventName}`);
        // Fetch all lectures from Google Sheets using dynamic sheet ID
        const allLectures = await fetchLectureData(config.google_sheet_id); // Pass sheetId
        console.log('Fetched all lectures:', allLectures);

        if (!Array.isArray(allLectures) || allLectures.length === 0) {
          throw new Error('No lecture data found');
        }

        // Filter lectures for this specific event by looking at event property from Column A
        // Match case-insensitive and trim whitespace
        const eventLectures = allLectures.filter(lecture => 
          lecture.event && lecture.event.trim().toLowerCase() === decodedEventName.toLowerCase().trim()
        );

        console.log(`Filtered lectures for event "${decodedEventName}":`, eventLectures);

        if (eventLectures.length === 0) {
          setError(`No lectures found for event "${decodedEventName}"`);
        } else {
          setLectures(eventLectures);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading lecture data:', err);
        setError('Failed to load lecture data. Please try again later.');
        setLoading(false);
      }
    };

    loadEventLectures();
  }, [decodedEventName, config, configLoading, configError, eventName]);

  // Determine which layout to use based on device type
  const Layout = isMobile() ? MobileLayout : DesktopLayout;

  // Handle config loading/error state before rendering
  if (configLoading) {
    return <p>Loading configuration...</p>;
  }

  if (configError) {
    return <p>Error loading configuration: {configError.message || configError}</p>;
  }

  // Render loading state
  if (loading) {
    return (
      <Layout>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '18px',
          color: theme.colors.primary
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '16px' }}>
              Loading lectures for {decodedEventName}...
            </div>
            <div style={{
              width: '40px',
              height: '40px',
              margin: '0 auto',
              border: `4px solid ${theme.colors.lightGray}`,
              borderTopColor: theme.colors.primary,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
        </div>
      </Layout>
    );
  }

  // Prepare the filtered and sorted lectures
  const displayLectures = getFilteredAndSortedLectures();

  return (
    <Layout>
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Back button and page header */}
        <div style={{ marginBottom: '24px' }}>
          <Link 
            to="/"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              color: theme.colors.primary,
              textDecoration: 'none',
              marginBottom: '16px',
              fontWeight: '500'
            }}
          >
            <span style={{ marginRight: '8px' }}>&larr;</span>
            Back to Homepage
          </Link>

          <h1 style={{
            fontSize: '32px',
            fontWeight: 'bold',
            marginBottom: '8px',
            color: theme.colors.headingColor || theme.colors.primary
          }}>
            {decodedEventName}
          </h1>

          <p style={{ color: theme.colors.darkGray, marginBottom: '24px' }}>
            {displayLectures.length} {displayLectures.length === 1 ? 'lecture' : 'lectures'} available
          </p>

          {/* Filter and sort controls */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            alignItems: 'center',
            marginBottom: '24px',
            backgroundColor: '#f0f2f5',
            color: '#333333',
            padding: '16px',
            borderRadius: theme.borderRadius.medium
          }}>
            {/* Sort dropdown */}
            <div>
              <label htmlFor="sort" style={{ marginRight: '8px', fontWeight: '500', color: '#333333' }}>
                Sort by:
              </label>
              <select 
                id="sort"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.colors.primary}30`,
                  backgroundColor: 'white',
                  color: '#333',
                  fontSize: '14px',
                  outline: 'none'
                }}
              >
                <option value="lectureIdInSeries-asc">Lecture # (Ascending)</option>
                <option value="lectureIdInSeries-desc">Lecture # (Descending)</option>
                <option value="date-desc">Date (Newest First)</option>
                <option value="date-asc">Date (Oldest First)</option>
                <option value="title-asc">Title (A-Z)</option>
                <option value="title-desc">Title (Z-A)</option>
              </select>
            </div>

            {/* Tag filter dropdown */}
            {getTags().length > 0 && (
              <div>
                <label htmlFor="tag" style={{ marginRight: '8px', fontWeight: '500', color: '#333333' }}>
                  Filter by tag:
                </label>
                <select 
                  id="tag"
                  value={filterTag}
                  onChange={(e) => setFilterTag(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">All Tags</option>
                  {getTags().map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Reset filters button */}
            {(filterTag || sortOption !== 'lectureIdInSeries-asc') && (
              <button
                onClick={() => {
                  setSortOption('lectureIdInSeries-asc');
                  setFilterTag('');
                }}
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  color: '#333333'
                }}
              >
                Reset
              </button>
            )}

            {/* Display count of filtered results */}
            <div style={{ marginLeft: 'auto', color: '#666666' }}>
              Showing {displayLectures.length} of {lectures.length} lectures
            </div>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            padding: '24px',
            textAlign: 'center',
            color: theme.colors.error,
            backgroundColor: '#FFEEEE',
            borderRadius: theme.borderRadius.medium,
            marginBottom: '24px'
          }}>
            <p>{error}</p>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: theme.colors.primary,
                color: 'white',
                border: 'none',
                padding: '10px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                marginTop: '16px'
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Lecture grid - matches homepage Detailed Lecture Notes style */}
        {!error && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile() ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: isMobile() ? '12px' : '20px',
            marginBottom: '40px'
          }}>
            {displayLectures.map((lecture, index) => {
              const hasLectureNumber = lecture.lectureIdInSeries && lecture.lectureIdInSeries.toString().trim() !== '';
              const videoId = extractVideoId(lecture.fullVideoUrl || lecture.summaryVideoUrl);
              const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;

              // For recurring events (no lecture number), show formatted date
              const getHeaderText = () => {
                if (hasLectureNumber) return `Lecture ${lecture.lectureIdInSeries}`;
                if (lecture.date) {
                  try {
                    return new Date(lecture.date).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric'
                    });
                  } catch { /* fall through */ }
                }
                return `Lecture ${index + 1}`;
              };

              return (
                <Link
                  key={lecture.lectureId}
                  to={`/lecture/${lecture.lectureId}`}
                  style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                >
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    cursor: 'pointer',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.05)';
                  }}
                  >
                    {/* Header with Lecture Number or Date */}
                    <div style={{
                      backgroundColor: theme.colors.primary,
                      padding: isMobile() ? '10px' : '14px',
                      textAlign: 'center',
                      color: 'white',
                      borderRadius: '12px 12px 0 0'
                    }}>
                      <div style={{
                        fontSize: isMobile() ? '14px' : '16px',
                        fontWeight: 'bold'
                      }}>
                        {getHeaderText()}
                      </div>
                    </div>

                    {/* YouTube Thumbnail */}
                    <div style={{
                      position: 'relative',
                      paddingTop: '56.25%',
                      backgroundColor: '#f3f4f6',
                      overflow: 'hidden'
                    }}>
                      {thumbnailUrl ? (
                        <img
                          src={thumbnailUrl}
                          alt={lecture.title}
                          style={{
                            position: 'absolute',
                            top: 0, left: 0,
                            width: '100%', height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div style={{
                          position: 'absolute',
                          top: '50%', left: '50%',
                          transform: 'translate(-50%, -50%)'
                        }}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                            <path d="M12 1C10.34 1 9 2.34 9 4V12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12V4C15 2.34 13.66 1 12 1Z" fill={theme.colors.primary} />
                            <path d="M17 11C17 11 17 12 17 12C17 14.76 14.76 17 12 17C9.24 17 7 14.76 7 12V11H5V12C5 15.64 7.81 18.64 11.28 18.97V22H12.72V18.97C16.19 18.64 19 15.64 19 12V11H17Z" fill={theme.colors.primary} />
                          </svg>
                        </div>
                      )}
                    </div>

                    {/* Grey Box with Title, Date, Speaker */}
                    <div style={{
                      backgroundColor: theme?.colors?.cardInfoBg || '#f0ece4',
                      background: `linear-gradient(to bottom, ${theme?.colors?.cardInfoBg || '#f0ece4'} 0%, ${theme?.colors?.cardInfoBgEnd || '#e8e3d9'} 100%)`,
                      padding: isMobile() ? '14px' : '18px',
                      textAlign: 'center',
                      minHeight: isMobile() ? '90px' : '110px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flex: 1
                    }}>
                      <h3 style={{
                        margin: '0 0 8px 0',
                        fontSize: isMobile() ? '13px' : '16px',
                        fontWeight: 'bold',
                        color: theme.colors.primary,
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {lecture.title || 'Untitled Lecture'}
                      </h3>
                      {lecture.date && (
                        <div style={{
                          fontSize: isMobile() ? '11px' : '12px',
                          color: '#6c757d',
                          marginTop: 'auto'
                        }}>
                          {new Date(lecture.date).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric'
                          })}
                          {lecture.speaker && (
                            <>
                              <span style={{ margin: '0 4px' }}>•</span>
                              <span>{lecture.speaker}</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {!error && displayLectures.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            backgroundColor: theme.colors.lightGray,
            borderRadius: theme.borderRadius.medium
          }}>
            <h3 style={{ marginBottom: '16px' }}>No lectures found</h3>
            <p>Try changing your filters or check back later for new content.</p>
          </div>
        )}
      </div>
      <FloatingShareButton 
        pageTitle={`${eventName || 'Event'} - ${config?.name || ''}`}
        pageDescription={`Explore lectures from ${eventName || 'this event'}`}
      />
    </Layout>
  );
};

export default EventPage;
